import { $, el } from '../state/store.js';
import { deleteFeedback, isFeedbackAdmin, listFeedback, markRead } from '../services/feedback.js';
import {
  anonCount, cleanupLog, confirmUser, createUser, deleteUser, isAdmin, listLicences,
  licenceHistory, notifyLicence, staleAnonCount,
  listUsers, removeLicence, setCredits, setLicence, setUserPassword
} from '../services/admin.js';
import { deleteTrial, listTrials, setTrialStatus } from '../services/trial.js';
import { sendPasswordReset } from '../services/auth.js';
import { tabStrip } from './controls.js';
import { calendarGrid, formatDate, formatDateTime, pickDate, toYMD } from './calendar.js';
import { fillContext } from './context-view.js';
import { logoLockup } from './logo.js';
import { toast } from './toast.js';
import { videoLinksPane } from './video-links.js';

/* THE ADMIN VIEW — who holds a licence, what people are asking about, and
   what people are reporting.

   One page with tabs rather than a button per job in the topbar. They are
   the same job (looking after customers) reached from the same place, and a
   second permanently-visible owner-only button is a permanent cost to every
   visitor's reading of the toolbar for something only one person clicks.

   A PAGE, not a window over the builder. It used to be a modal card, which
   made a long list of people something to scroll inside a box, and tied the
   customer inbox to a tab that also held somebody's unsaved scene. It is now
   the ?admin page (ui/admin-only.js) — its own tab, its own URL, the whole
   window — reached from the topbar's Admin link, which opens it beside the
   builder rather than replacing it.

   This absorbs what used to be ui/feedback-inbox.js wholesale; that file is
   gone. What it is NOT is the access control — the admin RLS in 0007 and 0010
   is. Anyone can reveal this screen; nothing they do from it will be permitted
   by the database unless their email is in feedback_admins. This only decides
   what is worth putting on screen.

   For why the licence table is keyed on email rather than user_id — so a
   licence can be granted before that account exists, and survives one being
   deleted and remade — see 0010_motion_licences.sql. */

/* ── dates ───────────────────────────────────────────────────────────────── */

/* Relative time, both directions. An expiry is only useful beside how far away
   it is: a bare date needs mental arithmetic every time, and the whole
   question being asked of that column is "is this one about to run out".
   The absolute date is shown too — see ui/calendar.js for why it is written
   dd.mm.yyyy here rather than however the browser would prefer. */
function relative(iso) {
  if (!iso) return 'no expiry';
  const ms = new Date(iso) - Date.now();
  const days = Math.round(Math.abs(ms) / 86400000);
  if (days === 0) return ms >= 0 ? 'expires today' : 'expired today';
  const span = days === 1 ? '1 day' : days + ' days';
  return ms >= 0 ? 'in ' + span : 'expired ' + span + ' ago';
}
const isExpired = iso => !!iso && new Date(iso) <= new Date();

/* An expiry is a whole day, not an instant: someone whose licence runs to the
   12th should still have it at teatime on the 12th. */
const endOfDay = ymd => ymd ? new Date(ymd + 'T23:59:59').toISOString() : null;

/* Storage, at the scale it is actually read at. Bytes for a handful of files
   is noise; the question this column answers is "are they anywhere near the
   200 MB ceiling in services/limits.js". */
function size(bytes) {
  const n = Number(bytes) || 0;
  if (!n) return '—';
  if (n < 1024 * 1024) return Math.max(1, Math.round(n / 1024)) + ' KB';
  const mb = n / (1024 * 1024);
  return (mb < 10 ? mb.toFixed(1) : Math.round(mb)) + ' MB';
}

function when(iso) {
  if (!iso) return 'never';
  const d = new Date(iso), diff = (Date.now() - d) / 1000;
  if (diff < 60) return 'just now';
  const n = Math.floor(diff / (diff < 3600 ? 60 : diff < 86400 ? 3600 : 86400));
  if (diff < 3600) return n + ' min ago';
  if (diff < 86400) return n + (n === 1 ? ' hour ago' : ' hours ago');
  if (diff < 604800) return n + (n === 1 ? ' day ago' : ' days ago');
  return formatDate(d);
}

/* THE HISTORY OF ONE LICENCE.

   motion_licences keeps only the current state — extending overwrites the old
   date, removing deletes the row — so before 0023 there was no way to answer
   "has this address had a trial already", which is precisely the question
   when somebody asks for a second one.

   Phrased as what happened, with both dates where both matter: "Extended to
   30.11.2026, from 30.10.2026" reads as an event, where a bare new value
   reads as a setting and loses the part worth knowing. */
function eventLine(e) {
  const to = e.now_until ? formatDate(e.now_until) : null;
  const from = e.was_until ? formatDate(e.was_until) : null;
  switch (e.action) {
    case 'granted':        return to ? 'Granted, until ' + to : 'Granted, no expiry';
    case 'extended':       return 'Extended to ' + to + ', from ' + from;
    case 'shortened':      return 'Shortened to ' + to + ', from ' + from;
    case 'expiry_set':     return 'End date set to ' + to;
    case 'expiry_cleared': return 'Expiry removed — was ' + from;
    case 'note_changed':   return 'Note changed';
    case 'removed':        return from ? 'Removed, had run to ' + from : 'Removed';
    default:               return e.action;
  }
}

/* ── users ───────────────────────────────────────────────────────────────── */

/* Deliberately not account.js's projectRow(). That one closes over its own
   module's hide() and hard-codes an Open/Overwrite/Duplicate/Delete set;
   bending it into a second job would couple two screens that have nothing to
   do with each other. Same chrome, different row.
 *
 * One row type for both lists. An account and a licence-without-an-account
 * differ in what is KNOWN about them, not in what you can do to them — the
 * actions are identical, so splitting the row would mean maintaining the same
 * four buttons twice.
 */
function personRow(p, refresh) {
  const licensed = !!p.licence_active;
  const lapsed = isExpired(p.licence_expires_at) && p.licence_expires_at;
  const item = el('div', 'projitem' + (lapsed ? ' is-expired' : ''));

  const who = el('div', 'licwho');
  who.appendChild(el('span', 'licemail', p.email));
  const sub = [];
  /* Three states, not two. `undefined` means the accounts list did not load,
     and "no account yet" is then a claim rather than a fact — the row says
     nothing rather than something it cannot know. */
  if (p.hasAccount === true) sub.push('last seen ' + when(p.last_sign_in_at));
  else if (p.hasAccount === false) sub.push('no account yet');
  if (p.storage_bytes) sub.push(size(p.storage_bytes) + ' · ' + p.asset_count
    + ' file' + (Number(p.asset_count) === 1 ? '' : 's'));
  /* An address that was never confirmed is worth seeing here, because it is
     the one that quietly cannot be reached — "Send reset" on it will look
     like it worked and arrive nowhere useful. */
  if (p.hasAccount && !p.email_confirmed_at) sub.push('email unconfirmed');
  if (p.note) sub.push(p.note);
  who.appendChild(el('span', 'licnote', sub.join(' · ')));
  item.appendChild(who);

  /* The licence state, said in one place. A pill rather than more text: this
     is the column the eye scans down looking for who is covered. */
  const state = el('span', 'licstate' + (licensed ? ' is-on' : lapsed ? ' is-off' : ''));
  state.textContent = licensed
    ? (p.licence_expires_at ? 'Motion · ' + formatDate(p.licence_expires_at) : 'Motion · no expiry')
    : lapsed ? 'Lapsed · ' + formatDate(p.licence_expires_at)
    : '—';
  if (p.licence_expires_at) state.title = relative(p.licence_expires_at);
  item.appendChild(state);

  /* Credits, next to the licence, because they answer the same question from
     the other side: what has this person been given. Shown even at zero — a
     blank would be indistinguishable from "this list does not know", which is
     the distinction the sub-line above is careful about. Not shown at all for
     a licence-without-an-account, where there is no balance to have. */
  if (p.hasAccount !== false) {
    const cr = el('span', 'licstate' + (p.credit_balance > 0 ? ' is-on' : ''));
    cr.textContent = (p.credit_balance || 0) + ' cr';
    cr.title = (p.credit_balance || 0) + ' export credit'
      + (p.credit_balance === 1 ? '' : 's');
    item.appendChild(cr);
  }

  const actions = el('div', 'presetactions');
  const actionBtn = (label, cls, run, confirmMsg) => {
    const b = el('button', 'btn btn--sm ' + cls, label);
    b.onclick = async () => {
      if (confirmMsg && !confirm(confirmMsg)) return;
      b.disabled = true;
      try { await run(); }
      catch (err) { toast(err.message || 'That didn’t work.', true); }
      finally { b.disabled = false; }
    };
    actions.appendChild(b);
    return b;
  };

  actionBtn(licensed || lapsed ? 'Edit' : 'Grant', licensed || lapsed ? 'btn--gh' : 'btn--pri', async () => {
    const ymd = await pickDate({
      title: 'Motion for ' + p.email,
      value: p.licence_expires_at ? toYMD(new Date(p.licence_expires_at)) : null
    });
    if (ymd === undefined) return;                 // dismissed, not cleared
    await setLicence(p.email, endOfDay(ymd), p.note || '');
    toast(licensed || lapsed ? 'Licence updated.' : 'Motion granted to ' + p.email + '.');
    await refresh();
  });

  /* Until a checkout exists this is the only way a balance goes up, and it is
     how "commercial use is arranged directly" is actually honoured: somebody
     pays by invoice, the owner types the number here. Offered only where
     there is an account to credit. */
  if (p.hasAccount !== false) {
    actionBtn('Credits', 'btn--gh', async () => {
      const now = p.credit_balance || 0;
      const typed = prompt(`Export credits for ${p.email}.

`
        + `A still costs 1, a video 10, a multi-panel video 20.`, String(now));
      if (typed === null) return;                       // dismissed, not cleared
      const n = Number(typed.trim());
      if (!Number.isInteger(n) || n < 0) throw new Error('Credits must be a whole number, zero or more.');
      if (n === now) return;
      await setCredits(p.email, n);
      toast(`${p.email} now has ${n} credit${n === 1 ? '' : 's'}.`);
      await refresh();
    });
  }

  /* Sends the customer a link; the owner never sees or sets the password
     itself. Says "if that address has an account" rather than confirming one
     exists, because resetPasswordForEmail deliberately does not report that
     — see services/auth.js. */
  actionBtn('Send reset', 'btn--gh', async () => {
    await sendPasswordReset(p.email);
    toast('Reset sent to ' + p.email + ', if that address has an account.');
  });

  /* "Remove licence", not "Remove". Alone it invited the question "remove
     what?" two buttons away from the one that deletes an account — and the row
     was mixing bare verbs with verb-plus-noun. Not "Revoke", which is the
     formally correct pair for Grant but is jargon this screen's one reader
     gains nothing from. */
  if (licensed || lapsed)
    actionBtn('Remove licence', 'btn--gh', async () => { await removeLicence(p.email); await refresh(); },
      'Remove ' + p.email + '’s Motion licence? They lose it immediately.');

  /* Everything below needs the service-role key and therefore the Edge
     Function — see services/admin.js. Offered only where there is an account
     to act on: a licence with nobody behind it yet has no password to set and
     nothing to delete. */
  if (p.hasAccount === true) {
    actionBtn('Password', 'btn--gh', async () => {
      const pw = prompt('New password for ' + p.email + '.\nAt least 8 characters. They can change it later.');
      if (pw === null) return;                       // cancelled
      await setUserPassword(p.email, pw);
      toast('Password set for ' + p.email + '. Send it to them yourself — it is not emailed.');
    });

    /* Only where it would do something. An address that is already confirmed
       has nothing to fix, and a button that always shows implies otherwise. */
    if (!p.email_confirmed_at)
      actionBtn('Confirm', 'btn--gh', async () => {
        await confirmUser(p.email);
        toast(p.email + ' confirmed.');
        await refresh();
      });

    /* "Delete user", not "Delete", and red where the others are not. Five
       buttons in a row all reading as equals is how the irreversible one gets
       clicked: every other action here is recoverable — a licence can be
       re-granted, a password re-set — and this one destroys somebody else's
       account and their work, with no undo available to them either.

       Its own class rather than colouring .btn--dgr, which is shared with the
       inspector rail's Remove. That one is a routine action used constantly
       while composing, and turning it permanently red would spend the alarm
       on something that does not warrant it — after which nothing would be
       left to mark the one that does. */
    actionBtn('Delete user', 'btn--danger', async () => { await deleteUser(p.email); await refresh(); },
      'Delete the account for ' + p.email + '? Their projects go with it.\n\n'
      + 'Any Motion licence stays — it belongs to the address, not the account — '
      + 'so signing up again restores it. Remove the licence separately if that '
      + 'is not what you want.');
  }

  item.appendChild(actions);

  /* A <details>, closed, UNDER the row rather than in it — .projitem is a
     single flex line and a disclosure that grows belongs beneath it.

     Fetched only when opened. Most rows are read to answer "is this person
     covered", which the pill already says; the history answers the narrower
     question that comes up when somebody asks for a second trial, and one
     query per person on every render would make the list slow for something
     hardly anybody expands. */
  const wrap = el('div', 'listrow');
  wrap.appendChild(item);

  const hist = el('details', 'lichist');
  hist.appendChild(el('summary', null, 'History'));
  const histBody = el('div', 'lichist__body', 'Loading…');
  hist.appendChild(histBody);
  let loaded = false;
  hist.addEventListener('toggle', async () => {
    if (!hist.open || loaded) return;
    loaded = true;
    const rows = await licenceHistory(p.email);
    histBody.textContent = '';
    if (!rows.length) {
      /* Said precisely. The trigger records from the day it shipped, so an
         empty history on an older licence means "nothing since then", not
         "nothing ever" — and the second is a claim this cannot make. */
      histBody.appendChild(el('p', 'authnote',
        'Nothing recorded. Changes have been logged since 2 September 2026.'));
      return;
    }
    rows.forEach(e => {
      const line = el('div', 'lichist__row');
      line.appendChild(el('span', 'lichist__when', formatDateTime(e.at)));
      line.appendChild(el('span', 'lichist__what', eventLine(e)));
      if (e.actor) line.appendChild(el('span', 'lichist__who', e.actor));
      histBody.appendChild(line);
    });
  });
  wrap.appendChild(hist);

  return wrap;
}

/* Set by usersPane so the Contact requests tab can refresh it after a grant.
   A module-level hook rather than an event, because both panes are rebuilt
   every time the page is built and a bus subscription would accumulate one
   listener per open with nothing to remove them. */
let refreshUsers = null;

function usersPane() {
  const pane = el('div');
  pane.appendChild(el('p', 'authnote',
    'Everyone who has signed up, and everyone holding a Motion licence — which '
    + 'are not the same list. A licence is granted to an email address, so it can '
    + 'be given before that person has an account and survives them making a new '
    + 'one. It also clears the export watermark.'));

  /* Granting to an address that has not signed up yet is a real case and it
     needs somewhere to be typed — the lists below can only offer rows that
     already exist. */
  const form = el('form', 'authform');
  const email = el('input');
  email.type = 'email'; email.required = true; email.placeholder = 'them@example.com';
  const note = el('input');
  note.type = 'text'; note.placeholder = 'Note (optional)';
  const add = el('button', 'btn btn--pri', 'Grant…');
  add.type = 'submit';
  form.append(email, note, add);
  pane.appendChild(form);

  /* Creating the account outright, for the case the emailed link cannot
     serve: the built-in mailer is rate-limited to a couple an hour, and a
     customer waiting on a link that will not arrive is a worse first
     impression than being handed a password. Confirmed on creation — see the
     Edge Function for why. */
  const mk = el('form', 'authform');
  const mkMail = el('input');
  mkMail.type = 'email'; mkMail.required = true; mkMail.placeholder = 'them@example.com';
  const mkPass = el('input');
  mkPass.type = 'text';            // shown, not masked: you have to read it out to them
  mkPass.required = true; mkPass.placeholder = 'password (min 8)';
  mkPass.autocomplete = 'off';
  const mkGo = el('button', 'btn btn--gh', 'Create account');
  mkGo.type = 'submit';
  mk.append(mkMail, mkPass, mkGo);
  mk.onsubmit = async ev => {
    ev.preventDefault();
    mkGo.disabled = true;
    try {
      await createUser(mkMail.value, mkPass.value);
      toast('Account created for ' + mkMail.value.trim().toLowerCase() + '. Send them the password yourself.');
      mkMail.value = ''; mkPass.value = '';
      await refresh();
    } catch (e) {
      toast(e.message || 'Couldn’t create that account.', true);
    } finally {
      mkGo.disabled = false;
    }
  };
  pane.appendChild(mk);
  pane.appendChild(el('p', 'note',
    'A password set here is never emailed — read it out or send it however you '
    + 'normally would. They can change it from Sign in → Forgot your password.'));

  const accountsHd = el('h3', null, 'Accounts');
  const accounts = el('div', 'projlist');
  const pendingHd = el('h3', 'hidden', 'Licensed, not signed up yet');
  const pending = el('div', 'projlist');
  const anonNote = el('p', 'authnote hidden');
  /* Its own block rather than more sentences in anonNote: this is about a
     scheduled job, not about the list, and it has to stay legible when the
     count above it says zero. */
  const cleanupNote = el('div', 'admincleanup');
  pane.append(accountsHd, accounts, anonNote, cleanupNote, pendingHd, pending);

  const refresh = async () => {
    accounts.textContent = '';
    accounts.appendChild(el('p', 'authnote', 'Loading…'));

    /* allSettled, NOT all. These are two independent questions — who has
       signed up, and who holds a licence — answered by two different
       migrations, and one failing must not blank the other. It did: with
       0011 unapplied, listUsers() rejected and took the licence list down
       with it, so a licence you had just granted was invisible because an
       unrelated query had failed. */
    const [uRes, lRes, aRes, sRes, cRes] = await Promise.allSettled(
      [listUsers(), listLicences(), anonCount(), staleAnonCount(), cleanupLog(5)]);
    const users = uRes.status === 'fulfilled' ? uRes.value : null;
    const licences = lRes.status === 'fulfilled' ? lRes.value : [];
    const anon = aRes.status === 'fulfilled' ? aRes.value : 0;
    const stale = sRes.status === 'fulfilled' ? sRes.value : null;
    const runs = cRes.status === 'fulfilled' ? cRes.value : [];

    const noteFor = {};
    licences.forEach(l => { noteFor[l.email.toLowerCase()] = l.note; });

    // ── accounts
    accounts.textContent = '';
    /* No filtering here any more: 0013 excludes anonymous sessions in the
       query, which is the only place it can be done correctly — doing it after
       the 500-row cap meant 971 anonymous rows filled the cap and both real
       accounts fell off the end. */
    const real = users || [];
    if (users === null) {
      accounts.appendChild(el('p', 'authnote', uRes.reason?.message || 'Couldn’t load accounts.'));
    } else if (!real.length) {
      accounts.appendChild(el('p', 'authnote', 'Nobody has signed up yet.'));
    } else {
      /* admin_list_users caps at 500 (0011). Said out loud rather than left as
         a short list that looks complete — a truncated answer you cannot see
         is worse than a slow one. */
      if (users.length >= 500)
        accounts.appendChild(el('p', 'authnote', 'Showing the 500 newest accounts.'));
      real.forEach(u => accounts.appendChild(personRow({
        email: u.email,
        hasAccount: true,
        last_sign_in_at: u.last_sign_in_at,
        email_confirmed_at: u.email_confirmed_at,
        licence_expires_at: u.licence_expires_at,
        licence_active: u.licence_active,
        credit_balance: u.credit_balance,
        storage_bytes: u.storage_bytes,
        asset_count: u.asset_count,
        note: noteFor[u.email.toLowerCase()]
      }, refresh)));
    }

    /* Anonymous sessions are accounts in the strict sense — every visitor gets
       one — but they carry no email, can hold no licence and can be acted on
       in no way. Counted rather than listed, so the list stays the people and
       not the traffic. The count is its own query for the same reason the
       filter is: it must not compete with customers for the row budget. */
    anonNote.textContent = anon
      ? anon + ' anonymous session' + (anon === 1 ? '' : 's') + ', not listed — no email, nothing to grant.'
      : '';
    anonNote.classList.toggle('hidden', !anon);

    /* THE CLEANUP, said out loud.
       A scheduled job that quietly deletes personal data is exactly the kind
       of thing that breaks without anyone noticing, so both halves are shown:
       how many are currently eligible, and when it last actually ran. Either
       number alone misleads — "0 eligible" reads as healthy whether the job
       is keeping up or has never been armed at all. */
    cleanupNote.textContent = '';
    if (stale === null) {
      cleanupNote.appendChild(el('p', 'authnote',
        'Cleanup status unavailable — migrations 0014/0015 may not be applied.'));
    } else {
      cleanupNote.appendChild(el('p', 'authnote',
        stale
          ? stale + ' of those ' + (stale === 1 ? 'is' : 'are') + ' idle past the '
            + '30-day window with nothing saved, and would be deleted on the next run.'
          : 'None are currently old enough and empty enough to delete.'));

      /* A dry run is not a deletion. Kept visually distinct rather than
         summed, because "ran on Tuesday" meaning "checked a number on
         Tuesday" is the misreading this whole panel exists to prevent. */
      const real = runs.filter(r => !r.dry_run);
      if (!runs.length) {
        /* Says only what it can see. It used to add "has not been armed",
           which stopped being true the moment the cron was armed and stayed
           on screen anyway — the run history cannot distinguish "no schedule"
           from "scheduled, not fired yet", so it should not claim either. The
           job runs at 04:00 UTC, so an empty history is normal on the first
           day. */
        cleanupNote.appendChild(el('p', 'authnote',
          'No runs recorded yet. The cleanup runs nightly; if this is still '
          + 'empty in a day or two, check the anon-cleanup job.'));
      } else {
        const last = runs[0];
        const when = new Date(last.ran_at).toLocaleString('en-GB',
          { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        cleanupNote.appendChild(el('p', 'authnote',
          'Last run ' + when + ' — '
          + (last.dry_run
              ? 'a dry run, nothing deleted (would have taken ' + last.deleted + ').'
              : 'deleted ' + last.deleted + '.')
          + (real.length
              ? ' ' + real.reduce((n, r) => n + r.deleted, 0) + ' deleted across the last '
                + real.length + ' real run' + (real.length === 1 ? '' : 's') + '.'
              : ' No real run has happened yet.')));
      }
    }

    // ── licences
    /* When the accounts list is unavailable, EVERY licence belongs here:
       "has this address signed up" is precisely the thing that just failed to
       load, so claiming any of them are orphans would be a guess. The heading
       changes to match what is actually being shown. */
    const known = new Set(real.map(u => u.email.toLowerCase()));
    const orphans = licences.filter(l => !known.has(l.email.toLowerCase()));
    pendingHd.textContent = users === null ? 'Motion licences' : 'Licensed, not signed up yet';
    pendingHd.classList.toggle('hidden', !orphans.length);
    pending.textContent = '';
    if (lRes.status === 'rejected')
      pending.appendChild(el('p', 'authnote', lRes.reason?.message || 'Couldn’t load licences.'));
    orphans.forEach(l => pending.appendChild(personRow({
      email: l.email,
      hasAccount: users !== null ? false : undefined,
      licence_expires_at: l.expires_at,
      licence_active: !l.expires_at || new Date(l.expires_at) > new Date(),
      note: l.note
    }, refresh)));
  };

  form.onsubmit = async ev => {
    ev.preventDefault();
    const addr = email.value.trim();
    if (!addr) return;
    const ymd = await pickDate({ title: 'Motion for ' + addr });
    if (ymd === undefined) return;
    add.disabled = true;
    try {
      await setLicence(addr, endOfDay(ymd), note.value);
      toast('Motion granted to ' + addr.toLowerCase() + '.');
      email.value = ''; note.value = '';
      await refresh();
    } catch (e) {
      toast(e.message || 'Couldn’t grant that.', true);
    } finally {
      add.disabled = false;
    }
  };

  /* Published for the Contact requests tab, which grants the same licences
     this pane lists. Cleared on the next open rather than never, because
     usersPane() runs again every time the page is built. */
  refreshUsers = refresh;
  refresh();
  return pane;
}

/* ── feedback ────────────────────────────────────────────────────────────── */

function contextBlock(ctx) {
  if (!ctx) return null;
  const det = el('details', 'fb-what');
  det.appendChild(el('summary', null, 'Their setup'));
  const body = el('div', 'fb-what-body');
  if (!fillContext(body, ctx, [['env', 'Their machine'], ['app', 'Their project']])) return null;
  det.appendChild(body);
  return det;
}

/* ── contact requests ────────────────────────────────────────────────────── */
/* Renamed from "trial requests" — there is no trial any more, every tool is
   free to try without asking. This is the queue for the two things still
   arranged by hand: a credit pack (no checkout yet) and an Advanced licence.
   The table, the RPCs (admin_list_trials, admin_set_trial_status) and the
   functions imported below keep their old names — a database rename is its
   own job, not this one. */

/* A default when nobody asked for a date. The form deliberately does not ask
   when they need it — that is a conversation, not a field — so a first grant
   lands on a standard length and you adjust it in the calendar if the answer
   turns out to be different. */
const TRIAL_DAYS = 30;
function defaultTrialEnd() {
  const d = new Date();
  d.setDate(d.getDate() + TRIAL_DAYS);
  return toYMD(d);
}

/* GRANTING A LICENCE IN REPLY — a date and, if you want, something to say.
   Its own dialog rather than pickDate(): granting answers a person, so the
   end date and the message that carries it belong in one step, and pickDate
   has three other callers whose return shape should not change to suit this
   one. It composes the same calendarGrid() they use, so there is still one
   calendar in the app.

   Resolves { ymd, note } or undefined for a dismissal — the same three-way
   distinction pickDate makes, minus "no expiry": a grant made here always has
   an end date, and the Users tab is where an open-ended licence is granted
   instead. */
function approveDialog({ email, company, defaultYmd }) {
  return new Promise(resolve => {
    const wrap = el('div', 'authwrap');
    const card = el('div', 'authcard authcard--cal');
    card.appendChild(el('h2', null, 'Grant a licence'));
    card.appendChild(el('p', 'authnote',
      email + (company ? ' · ' + company : '')
      + ' — they are emailed the end date as soon as you approve.'));

    let ymd = defaultYmd;
    const read = el('p', 'authnote', 'Runs until ' + formatDate(new Date(ymd + 'T12:00:00')));
    card.appendChild(read);
    card.appendChild(calendarGrid(new Date(ymd + 'T12:00:00'), d => {
      ymd = toYMD(d);
      read.textContent = 'Runs until ' + formatDate(d);
    }));

    const lab = el('label', 'trialfield');
    lab.appendChild(el('span', null, 'Add a note (optional)'));
    const note = el('textarea', 'trialinput trialmsg');
    note.rows = 3;
    note.maxLength = 2000;
    note.placeholder = 'Goes into the email, above your sign-off.';
    lab.appendChild(note);
    card.appendChild(lab);

    const row = el('div', 'authrow');
    const cancel = el('button', 'btn btn--sm btn--gh', 'Cancel');
    cancel.onclick = () => done(undefined);
    const ok = el('button', 'btn btn--sm btn--pri', 'Grant and email');
    ok.onclick = () => done({ ymd, note: note.value });
    row.append(cancel, ok);
    card.appendChild(row);

    function done(v) { wrap.remove(); resolve(v); }
    wrap.appendChild(card);
    document.body.appendChild(wrap);
    note.focus();
  });
}

function trialRow(t, refresh) {
  const open = t.status === 'new';
  const item = el('div', 'projitem' + (open ? ' is-new' : ''));

  const who = el('div', 'licwho');
  who.appendChild(el('span', 'licemail', t.email));
  /* What they came about, first in the line, since it decides what the reply
     leads with — it should not be hunted for at the end of a list of
     attributes. Not phrased as a locked surface any more (nothing is one);
     `source` just says which of the two things this is about. */
  const about = t.source === 'licence' ? 'about a licence' : 'about credits';
  const sub = [about, t.company, t.business_type, t.country, when(t.created_at)];
  /* What they have actually done with it, which is the difference between a
     real prospect and somebody who opened the page. Neither is a reason to
     refuse; both are worth knowing before you reply. */
  if (t.project_count) sub.push(t.project_count + ' project' + (t.project_count === 1 ? '' : 's'));
  who.appendChild(el('span', 'licnote', sub.filter(Boolean).join(' · ')));
  item.appendChild(who);

  const state = el('span', 'licstate'
    + (t.licence_active ? ' is-on' : open ? ' is-new' : ''));
  /* The licence, not the request, when there is one: "granted" on a row whose
     licence has since lapsed would be the wrong answer to the question this
     column exists to answer.

     And it carries the DATE, phrased exactly as personRow phrases it in the
     Users tab. Two rows describing the same licence in different words — one
     with an expiry, one saying only "active" — read as two different facts,
     and the date is the whole substance of a trial. */
  state.textContent = t.licence_active
      ? (t.licence_expires_at ? 'Motion · ' + formatDate(t.licence_expires_at)
                              : 'Motion · no expiry')
    : open ? 'New'
    : t.status === 'granted' ? 'Granted, lapsed'
    : 'Dismissed';
  if (t.licence_expires_at) state.title = relative(t.licence_expires_at);
  item.appendChild(state);

  const actions = el('div', 'presetactions');
  const act = (label, cls, run, confirmMsg) => {
    const b = el('button', 'btn btn--sm ' + cls, label);
    b.onclick = async () => {
      if (confirmMsg && !confirm(confirmMsg)) return;
      b.disabled = true;
      try { await run(); }
      catch (err) { toast(err.message || 'That didn’t work.', true); }
      finally { b.disabled = false; }
    };
    actions.appendChild(b);
    return b;
  };

  if (!t.licence_active) {
    /* "Approve", not "Grant" — unlike the Users tab, this button answers a
       request, and it sits beside Dismiss. Grant/Dismiss is a mismatched pair:
       one names what you confer, the other how you reply. Approve/Dismiss are
       two answers to the same question, which is what these two actually are.
       The Users tab keeps Grant, where nobody asked and you are conferring a
       right unprompted. */
    act(open ? 'Approve' : 'Approve again', open ? 'btn--pri' : 'btn--gh', async () => {
      const res = await approveDialog({
        email: t.email, company: t.company, defaultYmd: defaultTrialEnd()
      });
      if (res === undefined) return;                 // dismissed
      const expiresAt = endOfDay(res.ymd);
      /* Three calls, deliberately in this order. The licence is the thing
         that matters to the customer; filing the request is bookkeeping; the
         email is the courtesy. If a later one fails the earlier ones stand,
         which is recoverable — the other way round would tell somebody their
         licence is live when it is not. */
      await setLicence(t.email, expiresAt, 'Contact · ' + t.company);
      await setTrialStatus(t.id, 'granted');
      /* "granted", with a date — the Users tab row (personRow) already reads
         this together with the expiry, so the verb does not need to carry
         "for a while" on its own the way it did when this was a trial. */
      try {
        await notifyLicence(t.email, expiresAt, res.note);
        toast('Licence granted for ' + t.email + ' — they have been emailed.');
      } catch (mailErr) {
        /* Not thrown on to the row's error handler: that would report the
           whole approval as failed when the only thing that failed is the
           notice, and the licence is already live. */
        console.error('[illusio] licence mail failed', mailErr);
        toast('Licence granted for ' + t.email + ', but the email did not send: '
              + (mailErr.message || 'unknown error'), true);
      }
      await refresh();
      /* The Users tab is showing the same licence and was rendered before this
         happened. Leaving it stale means switching tabs to check your own work
         and finding it apparently undone. */
      if (refreshUsers) await refreshUsers();
    });
  }

  const mail = el('a', 'btn btn--sm btn--gh', 'Reply');
  mail.href = 'mailto:' + t.email
    + '?subject=' + encodeURIComponent('Your Illusio 3D request');
  actions.appendChild(mail);

  if (open) act('Dismiss', 'btn--gh', async () => {
    await setTrialStatus(t.id, 'dismissed');
    await refresh();
  }, 'Dismiss the request from ' + t.email + '?');

  /* Distinct from Dismiss, which is an answer and keeps the record. This is
     for rows that were never a request — tests, duplicates, spam — so it is
     red at rest like Delete user, and it is the only irreversible thing in
     this row. */
  act('Delete', 'btn--danger', async () => {
    await deleteTrial(t.id);
    await refresh();
  }, `Delete the request from ${t.email} entirely?

This cannot be undone. Dismiss keeps the record instead.`);

  item.appendChild(actions);

  /* Same wrapper class the Users tab uses: .projitem's own divider is turned
     off inside it, and the wrapper carries it instead, so the line falls
     between people rather than between a row and the thing hanging off it. */
  const wrap = el('div', 'listrow');
  wrap.appendChild(item);
  /* The message, when there is one. Below the row rather than inside it: the
     row is a scannable line and this is prose, and most requests will not
     have any. */
  if (t.message) {
    const q = el('div', 'trialmsgbox');
    q.textContent = t.message;
    wrap.appendChild(q);
  }
  return wrap;
}

function trialsPane(onCount) {
  const pane = el('div');
  const status = el('p', 'authnote', 'Loading…');
  const list = el('div', 'projlist');
  pane.append(status, list);

  const refresh = async () => {
    list.textContent = '';
    const rows = await listTrials(200);
    const open = rows.filter(r => r.status === 'new').length;
    onCount(open);
    status.textContent = rows.length
      ? rows.length + ' request' + (rows.length === 1 ? '' : 's') + ', ' + open + ' waiting.'
      : 'Nothing yet. Messages arrive here from the contact form — Buy credits, Ask about a '
        + 'licence, or the pricing page.';
    rows.forEach(t => list.appendChild(trialRow(t, refresh)));
  };
  refresh().catch(e => { status.textContent = e.message || 'Couldn’t load requests.'; });
  return pane;
}

function feedbackPane(onCount) {
  const pane = el('div');
  const status = el('p', 'authnote', 'Loading…');
  const list = el('div', 'inbox');
  const bar = el('div', 'authrow authrow--split');
  const allRead = el('button', 'btn btn--gh hidden', 'Mark all read');
  bar.appendChild(allRead);
  pane.append(status, list, bar);

  let items = [];
  const countLeft = () => items.filter(i => !i.read_at).length;

  const load = async () => {
    list.textContent = '';
    try { items = await listFeedback({ limit: 100 }); }
    catch (e) { status.textContent = e.message; return; }

    const unread = countLeft();
    status.textContent = items.length
      ? items.length + ' message' + (items.length === 1 ? '' : 's') + ', ' + unread + ' unread.'
      : 'Nothing yet. When someone uses the bug button, it turns up here.';
    allRead.classList.toggle('hidden', unread === 0);
    onCount(unread);

    for (const it of items) {
      const art = el('article', 'inbox__item' + (it.read_at ? '' : ' is-unread'));
      const head = el('div', 'inbox__head');
      head.appendChild(el('span', 'inbox__when', when(it.created_at)));
      if (it.reply_to) {
        const a = el('a', 'link', it.reply_to);
        a.href = 'mailto:' + it.reply_to + '?subject=Re: your Illusio 3D feedback';
        head.appendChild(a);
      } else {
        head.appendChild(el('span', 'inbox__noreply', 'no reply address'));
      }
      const mark = el('button', 'inbox__mark', it.read_at ? 'Mark unread' : 'Mark read');
      mark.onclick = async () => {
        mark.disabled = true;
        try {
          await markRead([it.id], !it.read_at);
          /* Mutated in place rather than reloading: the row you just clicked
             should not jump out from under the pointer. The topbar count does
             have to be told, though — the old inbox left it stale until the
             next auth change. */
          it.read_at = it.read_at ? null : new Date().toISOString();
          art.classList.toggle('is-unread', !it.read_at);
          mark.textContent = it.read_at ? 'Mark unread' : 'Mark read';
          allRead.classList.toggle('hidden', countLeft() === 0);
          onCount(countLeft());
        } catch (e) { toast(e.message, true); }
        finally { mark.disabled = false; }
      };
      head.appendChild(mark);

      /* Beside Mark read, and red, because it is the one action here that
         cannot be taken back — the row is gone, not flagged. Worth having:
         without it the inbox only ever grew, and test submissions stayed in
         it forever. */
      const del = el('button', 'inbox__mark inbox__mark--danger', 'Delete');
      del.onclick = async () => {
        if (!confirm(`Delete this message for good?

It is removed from the inbox and from the daily digest. This cannot be undone.`)) return;
        del.disabled = true;
        try {
          await deleteFeedback(it.id);
          /* Removed in place rather than reloading, for the reason the mark
             handler gives: the list should not jump. The count has to be told
             separately, and only if this one was unread. */
          const wasUnread = !it.read_at;
          items = items.filter(x => x.id !== it.id);
          art.remove();
          if (wasUnread) onCount(countLeft());
          allRead.classList.toggle('hidden', countLeft() === 0);
          status.textContent = items.length
            ? items.length + ' message' + (items.length === 1 ? '' : 's') + ', '
              + countLeft() + ' unread.'
            : 'Nothing yet. When someone uses the bug button, it turns up here.';
        } catch (e) { toast(e.message, true); del.disabled = false; }
      };
      head.appendChild(del);

      art.appendChild(head);
      art.appendChild(el('p', 'inbox__msg', it.message));
      const ctx = contextBlock(it.context);
      if (ctx) art.appendChild(ctx);
      list.appendChild(art);
    }
  };

  allRead.onclick = async () => {
    const unread = items.filter(i => !i.read_at).map(i => i.id);
    if (!unread.length) return;
    allRead.disabled = true;
    try { await markRead(unread, true); toast('All marked read.'); await load(); }
    catch (e) { toast(e.message, true); }
    finally { allRead.disabled = false; }
  };

  load();
  return pane;
}

/* ── the screen ──────────────────────────────────────────────────────────── */

/**
 * Build the admin page into `host`: a header saying who is signed in and the
 * way back to the app, then the tabs across the full width of the window.
 *
 * The panes are the same three that were in the modal, unchanged — only what
 * holds them differs. Their lists carry a scroll limit sized for a card
 * (.projlist, .inbox); on the page that limit is lifted (.apage in app.css) so
 * the PAGE scrolls once, rather than each list scrolling inside it.
 *
 * @param {HTMLElement} host   an empty element to fill
 * @param {{email?:string, onSignOut?:()=>void}} [opts]
 */
export function mountAdminPage(host, { email = '', onSignOut } = {}) {
  host.innerHTML = '';
  host.classList.add('apage');
  document.title = 'Admin — Illusio 3D';

  const bar = el('header', 'apage__bar');
  const h = el('h1', 'apage__h');
  h.appendChild(logoLockup());
  h.appendChild(el('span', 'mtop__sub', 'Admin'));
  bar.appendChild(h);
  bar.appendChild(el('span', 'apage__sp'));
  if (email) bar.appendChild(el('span', 'apage__who', email));
  const app = el('a', 'btn btn--sm btn--gh', 'Open the app');
  app.href = '/';
  bar.appendChild(app);
  if (onSignOut) {
    const out = el('button', 'btn btn--sm btn--gh', 'Sign out');
    out.onclick = onSignOut;
    bar.appendChild(out);
  }
  host.appendChild(bar);

  const main = el('main', 'apage__main');
  const videoLinks = videoLinksPane();
  const panes = [['Users', usersPane()],
                 ['Contact requests', trialsPane(n => paintBadge('trials', n))],
                 ['Feedback', feedbackPane(n => paintBadge('feedback', n))],
                 ['Video links', videoLinks]];
  /* key 'admin', not the rail's 'inspector': tabStrip's tabState is a
     module-level Map that is never cleared, so two callers sharing a key
     would move each other's tabs. */
  const tabs = tabStrip(panes, { key: 'admin' });
  main.appendChild(tabs);
  // Its data is fetched when its tab is first opened, not on every admin page load.
  tabs.querySelector('button[data-t="Video links"]')?.addEventListener('click', () => videoLinks.load());
  // The tab choice is remembered: reopening Admin on it has to load without a click.
  if (tabs.querySelector('button[data-t="Video links"][aria-selected="true"]')) videoLinks.load();
  panes.forEach(([, node]) => main.appendChild(node));
  host.appendChild(main);

  /* Counts already known are applied now rather than waiting for each pane's
     first load to report — otherwise the tabs sit unlabelled for as long as the
     slowest query takes. */
  paintTabCounts();
}

/* ── the topbar button ───────────────────────────────────────────────────── */

/* Two things need answering and both belong on one button. Kept as named
   parts rather than a running total, because each pane reports its own count
   independently and whichever loaded last would otherwise overwrite the
   other's number with its own. */
const badge = { feedback: 0, trials: 0 };

/* Which tab each count belongs on. The topbar button says how much is waiting
   in total; without this the tabs then make you open all three to find out
   where. Same "(n)" as the button rather than a badge of its own — it is the
   same fact, and inventing a second visual language for it would imply it
   was a different one. */
const TAB_FOR = { trials: 'Contact requests', feedback: 'Feedback' };

function paintTabCounts() {
  for (const kind of Object.keys(TAB_FOR)) {
    const title = TAB_FOR[kind];
    /* Matched on data-t, which tabStrip sets to the ORIGINAL title and reads
       back when deciding which tab is active — so the visible label can carry
       a count without the strip losing track of itself. */
    const tab = $('.apage .tabstrip button[data-t="' + title + '"]');
    if (tab) tab.textContent = badge[kind] ? title + ' (' + badge[kind] + ')' : title;
  }
}

function paintBadge(kind, n) {
  if (kind) badge[kind] = n || 0;
  const total = badge.feedback + badge.trials;
  const btn = $('#btnAdmin');
  /* Not an early return when the button is missing: the tabs are a separate
     surface and one being absent must not stop the other being painted. */
  if (btn) {
    /* "A", or "A (3)" with something waiting: the topbar is crowded, and the
       button says what it is by where it sits. The full name and the count are
       in the tooltip and the screen-reader name. */
    const name = total ? 'Admin, ' + total + ' waiting' : 'Admin';
    btn.textContent = total ? 'A (' + total + ')' : 'A';
    btn.title = name;
    btn.setAttribute('aria-label', name);
    btn.classList.toggle('btn--alert', total > 0);
  }
  paintTabCounts();
}

async function fetchBadge() {
  /* allSettled: two independent questions behind two different migrations,
     and one being unavailable must not leave the other uncounted. */
  const [f, t] = await Promise.allSettled([
    listFeedback({ unreadOnly: true, limit: 100 }),
    listTrials(200)
  ]);
  if (f.status === 'fulfilled') paintBadge('feedback', f.value.length);
  if (t.status === 'fulfilled')
    paintBadge('trials', t.value.filter(r => r.status === 'new').length);
}

/**
 * Reveal the button for admins and keep its unread count fresh. Asked of the
 * database rather than inferred from anything the client holds, so what is on
 * screen agrees with what a query will actually be permitted to do.
 *
 * Note ?admin is NOT that check. It selects a different boot — the panel alone,
 * no builder, so it can be used from a phone (ui/admin-only.js) — and grants
 * nothing: that URL on a non-admin account reaches exactly the empty lists the
 * server is willing to return.
 *
 * Checks both predicates: is_admin() is 0010's, is_feedback_admin() is 0007's,
 * and they read the same table — but a database that has not run 0010 yet
 * still has the second, and the inbox should not disappear waiting for a
 * migration.
 */
export function initAdmin() {
  const btn = $('#btnAdmin');
  if (!btn) return;
  /* No click handler: it is a link (index.html), to the admin page. */

  const refresh = async () => {
    const admin = await isAdmin().catch(() => false)
      || await isFeedbackAdmin().catch(() => false);
    btn.classList.toggle('hidden', !admin);
    if (admin) fetchBadge();
  };
  refresh();
  return refresh;
}
